import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Mail, Phone, Calendar, Reply, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ListToolbar, Pagination } from '../components/ListToolbar';
import {
  adminGetContactMessages, adminUpdateContactMessageStatus, adminExportMessages,
  adminSendEmail,
} from '../api/api';

const PAR_PAGE = 25;
const STATUTS = [
  { valeur: 'new', libelle: 'Nouveaux' },
  { valeur: 'read', libelle: 'Lus' },
  { valeur: 'replied', libelle: 'Répondus' },
];

const messageParDefaut = (m) => {
  const prenom = (m.name || '').trim().split(' ')[0] || 'Madame, Monsieur';
  return `Bonjour ${prenom},

Nous vous remercions de votre message et de l'intérêt que vous portez à NETTO+.



Nous restons à votre entière disposition pour toute information complémentaire.

Bien à vous,`;
};

// ---------------------------------------------------- Fenêtre de réponse
const FenetreReponse = ({ message, onClose, onEnvoye }) => {
  const [sujet, setSujet] = useState('');
  const [corps, setCorps] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (message) {
      setSujet('Votre message – NETTO+');
      setCorps(messageParDefaut(message));
    }
  }, [message]);

  const envoyer = async (e) => {
    e.preventDefault();
    if (!sujet.trim() || !corps.trim()) {
      toast.error('Objet et message sont requis.');
      return;
    }
    setEnvoi(true);
    try {
      await adminSendEmail({ to: message.email, subject: sujet, message: corps });
      if (message.status !== 'replied') {
        try {
          await adminUpdateContactMessageStatus(message.id, 'replied');
          onEnvoye(message.id, 'replied');
        } catch {
          onEnvoye(message.id, message.status);
        }
      } else {
        onEnvoye(message.id, message.status);
      }
      toast.success('Réponse envoyée.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L'envoi a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={!!message} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Répondre à {message?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dest">Destinataire</Label>
            <Input id="dest" value={message?.email || ''} disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sujet">Objet</Label>
            <Input id="sujet" value={sujet} onChange={(e) => setSujet(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="corps">Message</Label>
            <Textarea id="corps" rows={10} value={corps} onChange={(e) => setCorps(e.target.value)} required />
          </div>
          <p className="text-xs text-gray-500">
            Envoyé depuis contact@nettoplus.be. Votre signature (nom, rôle, logo) est ajoutée
            automatiquement, et les réponses reviennent sur contact@nettoplus.be.
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={envoi} className="gap-2">
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {envoi ? 'Envoi…' : 'Envoyer la réponse'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  const [loading, setLoading] = useState(true);
  const [repondre, setRepondre] = useState(null);

  useEffect(() => {
    const minuteur = setTimeout(() => {
      setLoading(true);
      adminGetContactMessages({
        skip,
        limit: PAR_PAGE,
        ...(recherche ? { search: recherche } : {}),
        ...(statut ? { status: statut } : {}),
      })
        .then((data) => {
          setMessages(data.items);
          setTotal(data.total);
        })
        .catch(() => toast.error('Erreur lors du chargement'))
        .finally(() => setLoading(false));
    }, recherche ? 350 : 0);
    return () => clearTimeout(minuteur);
  }, [skip, recherche, statut]);

  useEffect(() => {
    setSkip(0);
  }, [recherche, statut]);

  const majStatutLocal = (id, status) => {
    setMessages((liste) => liste.map((m) => (m.id === id ? { ...m, status } : m)));
  };

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateContactMessageStatus(id, status);
      toast.success('Statut mis à jour');
      majStatutLocal(id, status);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      read: 'bg-yellow-100 text-yellow-800',
      replied: 'bg-green-100 text-green-800'
    };
    return colors[status] || colors.new;
  };

  const getStatusLabel = (status) => {
    const labels = { new: 'Nouveau', read: 'Lu', replied: 'Répondu' };
    return labels[status] || status;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Messages de contact</h2>
        <p className="text-gray-600">Les messages envoyés depuis le formulaire de contact.</p>
      </div>

      <ListToolbar
        recherche={recherche}
        onRecherche={setRecherche}
        statut={statut}
        onStatut={setStatut}
        statuts={STATUTS}
        onExport={adminExportMessages}
        total={total}
        libelleTotal="message"
      />

      {loading && (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && messages.length === 0 && (
        <Card className="p-12 text-center text-gray-500">
          {recherche || statut
            ? 'Aucun message ne correspond à ces critères.'
            : 'Aucun message pour le moment.'}
        </Card>
      )}

      <div className="space-y-4">
        {!loading && messages.map((message) => (
          <Card key={message.id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{message.name}</h3>
                  <Badge className={getStatusColor(message.status)}>
                    {getStatusLabel(message.status)}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${message.email}`} className="hover:text-blue-600">
                      {message.email}
                    </a>
                  </div>
                  {message.phone && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${message.phone}`} className="hover:text-blue-600">
                        {message.phone}
                      </a>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.message}</p>
                </div>

                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(message.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              </div>

              <div className="flex flex-col space-y-2 ml-4">
                <Button
                  size="sm"
                  onClick={() => setRepondre(message)}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  <Reply className="w-4 h-4" />
                  Répondre
                </Button>
                {message.status === 'new' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(message.id, 'read')}
                  >
                    Marquer lu
                  </Button>
                )}
                {message.status !== 'replied' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(message.id, 'replied')}
                  >
                    Marquer répondu
                  </Button>
                )}
                {message.status !== 'new' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(message.id, 'new')}
                  >
                    Marquer nouveau
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Pagination skip={skip} limit={PAR_PAGE} total={total} onChange={setSkip} />

      <FenetreReponse
        message={repondre}
        onClose={() => setRepondre(null)}
        onEnvoye={majStatutLocal}
      />
    </div>
  );
};
